<template>
  <div class="timeline-container">
    <!-- Pinned Notes Section -->
    <div
      v-if="pinnedNotes && pinnedNotes.length > 0"
      class="pinned-section"
    >
      <div
        class="pinned-header"
        @click="isPinnedExpanded = !isPinnedExpanded"
      >
        <div class="flex items-center gap-2">
          <Icon
            name="mdi:pin"
            size="20"
            class="text-amber-600"
          />
          <span class="font-semibold text-gray-800">
            Pinned Notes
          </span>
          <Badge
            v-if="pinnedNotes"
            :value="pinnedNotes.length"
            size="small"
            severity="warning"
          />
        </div>
        <Icon
          :name="isPinnedExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'"
          size="20"
          class="text-gray-600"
        />
      </div>

      <div
        v-show="isPinnedExpanded"
        class="pinned-notes-list space-y-4 mt-4"
      >
        <div
          v-for="record in pinnedNotes || []"
          :key="record.id"
          class="pinned-note"
        >
          <div v-if="record.type === 'Notes' && record.Note">
            <NoteView
              :note="record.Note"
              @refresh="$emit('refresh')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline Groups -->
    <div
      v-if="groupedTimeline.length > 0"
      class="timeline-groups"
    >
      <div
        v-for="(group, groupIndex) in groupedTimeline"
        :key="group.date"
        class="timeline-group"
      >
        <!-- Date Header -->
        <div class="date-header">
          <h3 class="text-sm font-semibold uppercase text-gray-600">
            {{ group.displayDate }}
          </h3>
        </div>

        <!-- Timeline Items -->
        <div class="timeline-items">
          <div
            v-for="(record, recordIndex) in group.items"
            :key="record.id"
            class="timeline-item"
          >
            <!-- Timeline Line - Show for all items except the very last one across all groups -->
            <div
              v-if="!isLastItemInTimeline(groupIndex, recordIndex, group.items.length)"
              class="timeline-line"
            />

            <!-- Timeline Icon -->
            <div
              class="timeline-icon"
              :class="getIconClass(record)"
            >
              <Icon
                :name="getEventIcon(record)"
                size="18"
              />
            </div>

            <!-- Content Card -->
            <div class="timeline-content">
              <!-- Note -->
              <div
                v-if="record.type === 'Notes' && record.Note && !record.Note.isArchived"
              >
                <NoteView
                  :note="record.Note"
                  :show-actions="showActions"
                  @refresh="$emit('refresh')"
                />
              </div>

              <!-- Task -->
              <div
                v-else-if="record.type === 'Tasks' && record.Task && !record.Task.isArchived"
              >
                <TaskView
                  :task="record.Task"
                  :show-actions="showActions"
                  :users="users"
                  @refresh="$emit('refresh')"
                />
              </div>

              <!-- System Log -->
              <div
                v-else-if="record.type === 'SystemLogs' && record.SystemLog"
              >
                <SystemLogView
                  :system-log="record.SystemLog"
                  :show-actions="showActions"
                />
              </div>

              <!-- SMS Message -->
              <div
                v-else-if="record.type === 'SmsMessages' && record.SmsMessage"
              >
                <SmsMessageView
                  :sms-message="record.SmsMessage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!isLoading"
      class="empty-state"
    >
      <div class="flex flex-col items-center justify-center py-8 text-center">
        <div class="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
          <Icon
            name="mdi:timeline-clock-outline"
            size="32"
            class="text-gray-300"
          />
        </div>
        <p class="mb-1 text-[15px] font-semibold text-gray-800">
          No activity yet
        </p>
        <p class="text-xs text-gray-500">
          Once you start adding notes, tasks or updates, they'll show up here.
        </p>
      </div>
    </div>

    <!-- Load More -->
    <div
      v-if="hasMore && !isFetchingMore"
      class="load-more-section text-center mt-6"
    >
      <Button
        label="Load More"
        @click="$emit('loadMore')"
      />
    </div>

    <!-- Loading More -->
    <div
      v-if="isFetchingMore"
      class="text-center mt-6 text-gray-500"
    >
      <div class="flex items-center justify-center gap-2">
        <i class="pi pi-spin pi-spinner" />
        <span>Loading more activities...</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Timeline, Note, Task, SystemLog, SmsMessage, User } from '@prisma/client'
import { NoteType } from '@prisma/client'
import moment from 'moment'
import { NoteTypeIcon, NoteTypeColor, SystemLogTypeIcon, SystemLogTypeColor, SmsDirectionIcon, SmsDirectionColor } from './types'

type TimelineWithRelations = Timeline & {
  Note?: Note | null
  Task?: Task | null
  SystemLog?: SystemLog | null
  SmsMessage?: (SmsMessage & { Business?: { id: string, name: string } | null, Contact?: { id: string, firstName: string, lastName: string } | null }) | null
}

interface GroupedTimeline {
  date: string
  displayDate: string
  items: TimelineWithRelations[]
}

const props = defineProps<{
  timeline: TimelineWithRelations[]
  pinnedNotes?: TimelineWithRelations[]
  isLoading?: boolean
  isFetchingMore?: boolean
  hasMore?: boolean
  showActions?: boolean
  users: User[]
}>()

defineEmits<{
  loadMore: []
  refresh: []
}>()

const isPinnedExpanded = ref(true)

const isLastItemInTimeline = (groupIndex: number, recordIndex: number, groupLength: number): boolean => {
  if (groupedTimeline.value.length === 0) return false
  const isLastGroup = groupIndex === groupedTimeline.value.length - 1
  const isLastInGroup = recordIndex === groupLength - 1
  return isLastGroup && isLastInGroup
}

const groupedTimeline = computed<GroupedTimeline[]>(() => {
  if (!props.timeline || props.timeline.length === 0) {
    return []
  }

  const groups = new Map<string, TimelineWithRelations[]>()

  props.timeline.forEach((record) => {
    const date = new Date(record.createdAt)
    const dateKey = moment(date).format('YYYY-MM-DD')

    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(record)
  })

  // Convert to array and format
  const now = moment()
  const result: GroupedTimeline[] = []

  // Sort dates (newest first)
  const sortedDates = Array.from(groups.keys()).sort((a, b) => {
    return moment(b).diff(moment(a))
  })

  sortedDates.forEach((dateKey) => {
    const items = groups.get(dateKey)!

    // Sort items within group by time (newest first)
    items.sort((a, b) => {
      return moment(b.createdAt).diff(moment(a.createdAt))
    })

    // Format display date
    const dateMoment = moment(dateKey)
    let displayDate: string

    if (dateMoment.isSame(now, 'day')) {
      displayDate = 'Today'
    }
    else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      displayDate = 'Yesterday'
    }
    else if (dateMoment.isSame(now, 'week')) {
      displayDate = dateMoment.format('dddd')
    }
    else if (dateMoment.isSame(now, 'year')) {
      displayDate = dateMoment.format('MMM D')
    }
    else {
      displayDate = dateMoment.format('MMM D, YYYY')
    }

    result.push({
      date: dateKey,
      displayDate,
      items,
    })
  })

  return result
})

// Get event icon based on type
const getEventIcon = (record: TimelineWithRelations): string => {
  if (record.type === 'Notes' && record.Note) {
    const noteType = record.Note.type
    if (noteType in NoteTypeIcon) {
      return NoteTypeIcon[noteType]
    }
    return NoteTypeIcon[NoteType.Note]
  }

  if (record.type === 'Tasks') {
    return 'mdi:checkbox-marked-circle-outline'
  }

  if (record.type === 'SystemLogs' && record.SystemLog) {
    const logType = record.SystemLog.type
    if (logType in SystemLogTypeIcon) {
      return SystemLogTypeIcon[logType]
    }
    return 'mdi:information-outline'
  }

  if (record.type === 'SmsMessages' && record.SmsMessage) {
    const direction = record.SmsMessage.direction
    if (direction in SmsDirectionIcon) {
      return SmsDirectionIcon[direction]
    }
    return 'mdi:message-text-outline'
  }

  return 'mdi:circle-outline'
}

const getIconClass = (record: TimelineWithRelations): string => {
  if (record.type === 'Notes' && record.Note) {
    const noteType = record.Note.type

    const color = NoteTypeColor[noteType]
    if (!color) {
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    return `bg-${color}-100 text-${color}-600 border-${color}-300`
  }

  if (record.type === 'Tasks' && record.Task) {
    if (record.Task.isCompleted) {
      return 'bg-green-100 text-green-600 border-green-300'
    }
    return 'bg-orange-100 text-orange-600 border-orange-300'
  }

  if (record.type === 'SystemLogs' && record.SystemLog) {
    const logType = record.SystemLog.type
    const color = SystemLogTypeColor[logType]
    if (!color) {
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    return `bg-${color}-100 text-${color}-600 border-${color}-300`
  }

  if (record.type === 'SmsMessages' && record.SmsMessage) {
    const direction = record.SmsMessage.direction
    const color = SmsDirectionColor[direction]
    if (!color) {
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    return `bg-${color}-100 text-${color}-600 border-${color}-300`
  }

  return 'bg-gray-100 text-gray-600 border-gray-300'
}
</script>

<style scoped>
.timeline-container {
  position: relative;
  padding: 1rem 0.5rem;
}

.pinned-section {
  background: #FEF3C7;
  border: 2px solid #FCD34D;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pinned-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.pinned-notes-list {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pinned-note {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
}

.timeline-groups {
  position: relative;
}

.timeline-group {
  margin-bottom: 2.5rem;
}

.date-header {
  margin-bottom: 1rem;
  padding-left: 3.5rem;
  position: sticky;
  top: 0;
  background: white;
  z-index: 5;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

/* Timeline Items */
.timeline-items {
  position: relative;
  padding-left: 1rem;
}

.timeline-item {
  position: relative;
  display: flex;
  gap: 1rem;
  padding-bottom: 1.5rem;
  min-height: 60px;
}

.timeline-line {
  position: absolute;
  left: 1.25rem;
  top: 2.5rem;
  bottom: -1.5rem;
  width: 2px;
  background: #E5E7EB;
  z-index: 1;
  margin-left: 0;
}

.timeline-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
  background: white;
  border: 2px solid;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  margin-left: 0;
}

.timeline-item:hover .timeline-icon {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.timeline-content {
  flex: 1;
  min-width: 0;
  padding-top: 0.25rem;
}

.empty-state {
  padding: 2rem 1rem;
}

.load-more-section {
  padding: 1rem;
}

@media (max-width: 640px) {
  .timeline-container {
    padding: 0.5rem 0.25rem;
  }

  .date-header {
    padding-left: 2.5rem;
    font-size: 0.75rem;
  }

  .timeline-icon {
    width: 2rem;
    height: 2rem;
  }

  .timeline-line {
    left: 1rem;
  }

  .timeline-items {
    padding-left: 0.5rem;
  }

  .pinned-section {
    padding: 0.75rem 1rem;
  }
}
</style>
